import { useContext, useEffect, useMemo } from 'react';
import { AuthContext } from './AuthProvider';
import { useNavigate } from 'react-router';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import readingViewTheme from '../../../ReadingView/readerTheme';
import { ALMA_INSIGHTS_CALLBACK_URL, JaaRoute } from '../../utilities/Links';

export const LoginCallback = () => {
  const { actions, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Check if user was originally from ReadingView
  const contextInfo = useMemo(() => {
    const originalUrl =
      sessionStorage.getItem('original-url-before-redirect') ?? '/';
    const isFromReadingView = originalUrl.startsWith('/lue/');

    return {
      isFromReadingView,
      originalUrl,
    };
  }, []);

  useEffect(() => {
    const handleAuth = async () => {
      if (!loading && actions) {
        await actions.handleAuthentication();
        // Force refresh user info to ensure we have the latest data after SSO login
        await actions.getUserInfoWithoutCache();

        /* If the originalUrl is the /register route, that means the user accessed
        the route directly (for example from an email link). When the original 
        url is the /register route, we do not want to return the user back to
        JAA or the registration again from login callback.
        
        Instead, we redirect the user to the Sisältöpalvelut "Omat sisällöt"
        page, which is the more useful page for the user after registration. */
        if (
          contextInfo.originalUrl === JaaRoute.register ||
          contextInfo.originalUrl.startsWith(`${JaaRoute.register}?`)
        ) {
          window.location.replace(ALMA_INSIGHTS_CALLBACK_URL);
          return;
        }

        navigate(contextInfo.originalUrl);
      }
    };

    handleAuth();
  }, [actions, navigate, loading, contextInfo.originalUrl]);

  // If user was from ReadingView, render with ReadingView theme
  if (contextInfo.isFromReadingView) {
    const customTheme = createTheme(readingViewTheme);

    return (
      <ThemeProvider theme={customTheme} noSsr>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'background.default',
          }}
        >
          <Stack
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
            spacing={2}
          >
            <CircularProgress variant='indeterminate' />
            <Typography
              component='h1'
              variant='h5'
              sx={{ fontWeight: 600, textAlign: 'center' }}
            >
              Ladataan lukuympäristöä...
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: '300px',
              }}
            >
              Autentikoidaan käyttäjää ja varmistetaan käyttöoikeudet...
            </Typography>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  // Default main site loading
  return (
    <Box
      sx={{
        height: '800px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
        spacing={2}
      >
        <CircularProgress variant='indeterminate' />
        <Typography
          component='h1'
          variant='h5'
          sx={{ fontWeight: 600, textAlign: 'center' }}
        >
          Viimeistellään kirjautumista...
        </Typography>
      </Stack>
    </Box>
  );
};
